import { Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DefectLineForm } from './defect-line-form';
import type { CreateTicketFormData, formDefectLineSchema } from '@core/utils/validation';
import type { ImageResponse } from '@core/types/ticket';
import type { z } from 'zod';

const MAX_DEFECT_LINES = 10;
type DefectLineFormData = z.infer<typeof formDefectLineSchema>;

interface DefectLinesListProps {
  disabled?: boolean;
  existingImagesMap?: Map<number, ImageResponse[]>;
  onExistingImageRemove?: (lineIndex: number, imageIndex: number) => void;
  /** Override delete handler (e.g., for API delete in edit mode). Receives line index and a removeFn to call after successful API deletion. */
  onDeleteDefectLine?: (lineIndex: number, removeFn: () => void) => void;
  /** Minimum number of defect lines allowed. Default: 1 (create page). Set to 0 for edit page to allow deleting all lines. */
  minLines?: number;
}

export function DefectLinesList({
  disabled = false,
  existingImagesMap,
  onExistingImageRemove,
  onDeleteDefectLine,
  minLines = 1,
}: DefectLinesListProps) {
  const { control } = useFormContext<CreateTicketFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'defectLines',
  });

  const handleAddLine = () => {
    if (disabled) return;
    if (fields.length < MAX_DEFECT_LINES) {
      const newLine: DefectLineFormData = {
        lineType: 'Machine',
        componentItemCode: null,
        isOtherComponent: false,
        otherComponentDescription: '',
        marketDefect: null,
        isOtherDefect: false,
        otherDefectDescription: '',
        marketDefectCause: null,
        isOtherCause: false,
        otherCauseDescription: '',
        apiDefectLineName: undefined,
        images: [],
      };
      append(newLine);
    }
  };

  const handleRemoveLine = (index: number) => {
    if (disabled) return;
    if (fields.length > minLines) {
      // If custom delete handler provided (e.g., for API delete in edit mode), use it
      if (onDeleteDefectLine) {
        onDeleteDefectLine(index, () => remove(index));
      } else {
        remove(index);
      }
    }
  };

  const canAddMore = fields.length < MAX_DEFECT_LINES;
  const canDelete = fields.length > minLines;

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Thông tin lỗi
      </Typography>

      {fields.map((field, index) => (
        <DefectLineForm
          key={field.id}
          lineNumber={index + 1}
          onDelete={() => handleRemoveLine(index)}
          isOnlyLine={!canDelete}
          fieldName={`defectLines.${index}`}
          disabled={disabled}
          existingImages={existingImagesMap?.get(index)}
          onExistingImageRemove={onExistingImageRemove ? (imageIndex) => onExistingImageRemove(index, imageIndex) : undefined}
        />
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddLine}
        disabled={disabled || !canAddMore}
        fullWidth
        sx={{ mt: 2 }}
        title={canAddMore ? 'Thêm lỗi khác' : 'Tối đa 10 dòng lỗi'}
      >
        Thêm lỗi khác
      </Button>
    </Box>
  );
}
